import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ConfigCard from './ConfigCard';

// The fixed/required pages every webshop has. Their order is meaningful
// (matches the legacy admin) so admins can scan them in a consistent place.
// Each entry can be edited via its own page editor (handled by the parent
// app — this card just emits an `onEditPage(slug)` callback).
const REQUIRED_PAGES = [
    { slug: 'about',       i18nKey: 'cfg_footer_pages_about',       fallback: 'About' },
    { slug: 'terms',       i18nKey: 'cfg_footer_pages_terms',       fallback: 'Terms & conditions' },
    { slug: 'privacy',     i18nKey: 'cfg_footer_pages_privacy',     fallback: 'Privacy' },
    { slug: 'disclaimer',  i18nKey: 'cfg_footer_pages_disclaimer',  fallback: 'Disclaimer' },
];

/**
 * "Pages and links" card. Renders the required pages with an external URL
 * override and an "Edit page" button per row. If the URL is empty, the
 * storefront serves the internal content; otherwise it links externally.
 *
 * value shape:
 *   { about: 'https://...' | '',
 *     terms: '...',
 *     privacy: '...',
 *     disclaimer: '...' }
 *
 * onEditPage(slug) — parent decides what "Edit page" does (modal, route, …).
 */
const PagesAndLinksCard = ({ value = {}, onChange, onEditPage, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb = '') => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    return (
        <ConfigCard
            title={tr('cfg_footer_pages_title', 'Pages and links')}
            description={tr('cfg_footer_pages_desc', 'Required pages — these always exist. Leave the URL empty to use the internal page.')}
            theme={theme}
        >
            <Stack spacing={1.5}>
                {REQUIRED_PAGES.map(({ slug, i18nKey, fallback }) => (
                    <Box key={slug}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            {tr(i18nKey, fallback)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                            <TextField
                                size="small"
                                fullWidth
                                value={get(slug)}
                                onChange={(e) => set(slug)(e.target.value)}
                                placeholder={tr('cfg_footer_pages_url_placeholder', 'Leave empty to use the internal page')}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => onEditPage && onEditPage(slug)}
                                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                                {tr('cfg_footer_pages_edit', 'Edit page')}
                            </Button>
                        </Box>
                    </Box>
                ))}
            </Stack>
        </ConfigCard>
    );
};

PagesAndLinksCard.propTypes = {
    value:      PropTypes.object,
    onChange:   PropTypes.func,
    onEditPage: PropTypes.func,
    theme:      PropTypes.string,
    t:          PropTypes.func,
};

export default PagesAndLinksCard;
